// ---------------------------------------------------------------------------
// The disconnect sheet: this player dropped, and the round did not stop.
//
// The rule this component exists to communicate, from CLAUDE.md:
//
//   A server pause freezes everyone's clock, because the server decided and
//   that is fair. One player's disconnect freezes nobody's clock, including
//   their own, because freezing one player's clock while the others run is a
//   free-thinking-time exploit.
//
// Which is why the clock in here is RUNNING. `variant='frozen'` must never be
// rendered from this file: a stopped disc would show a disconnected player time
// they do not have, they would spend it thinking, and they would rightly believe
// they had been cheated out of it when the round scored without them.
//
// Everything else is deliberately the opposite of `ServerPauseOverlay` so the
// two cannot be mistaken for one another: a bottom sheet rather than a
// full-screen scrim, an orange top border rather than a neutral one, a live
// board above it rather than a greyed one, and a badge that says the round is
// still going.
//
// ALL OF WHICH IS ONLY TRUE INSIDE A ROUND. Everything above — the clock, the
// badge, the sentence about a saved answer — is a claim about a round with this
// player's answer riding on it, and the sheet was making all three in the lobby
// and between rounds, where they are simply false. `isLiveRoundPhase` is the
// engine's answer to which phases those are and this file does not re-derive it.
// Outside one, the sheet is a reconnecting notice and nothing else.
// ---------------------------------------------------------------------------

import { isLiveRoundPhase } from '@countdown/shared';
import type { Clock, GamePhase } from '@countdown/shared';
import { LiveClock, SignalBars } from '../primitives/index.js';
import { useClock } from '../../hooks/useClock.js';

export interface DisconnectSheetProps {
  /**
   * The room's phase, which decides whether there is a round to talk about at
   * all. Nothing below the sheet's heading survives a phase with no live clock.
   */
  phase: GamePhase;
  /**
   * The round clock. Rendered running, never frozen. Null outside a round, in
   * which case there is no clock to drain and the disc reads empty.
   */
  clock: Clock | null;
  /**
   * Letters the server has acknowledged in this player's answer. Null when the
   * caller cannot say, which renders a sentence that does not name a number.
   */
  savedAnswerLength: number | null;
  /**
   * The room's `pauseOnDisconnect` setting. It changes only the closing note:
   * players ask why the round did not stop, and the honest answer is that a host
   * can choose to make it stop and this host either did or did not.
   *
   * It does not change the badge or the clock. When the setting is on, the
   * server pauses the room for the drop, `state.serverPaused` goes true, and
   * `ServerPauseOverlay` covers this sheet with its own full-screen scrim. The
   * "everything stopped" message comes from there, never from here.
   */
  pauseOnDisconnect: boolean;
}

const LETTER_WORDS = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
];

const CLOCK_SENTENCE = 'The clock keeps going while we get you back.';

/** Never says "eight" unless eight letters really did reach the server. */
function savedLine(length: number | null): string {
  if (length === null) {
    return `We cannot say how much of your answer reached the server. ${CLOCK_SENTENCE}`;
  }
  if (length <= 0) {
    return `Nothing of your answer has reached the server yet. ${CLOCK_SENTENCE}`;
  }
  const word = LETTER_WORDS[length] ?? String(length);
  const letters = length === 1 ? 'one letter' : `${word} letters`;
  return `Your answer is saved at ${letters}. ${CLOCK_SENTENCE}`;
}

export function DisconnectSheet({
  phase,
  clock,
  savedAnswerLength,
  pauseOnDisconnect,
}: DisconnectSheetProps) {
  const reading = useClock(clock);
  const inRound = isLiveRoundPhase(phase);

  return (
    <div
      data-overlay="disconnect"
      role="status"
      aria-live="assertive"
      className="sheet-up fixed right-0 bottom-0 left-0 z-40 flex flex-col items-center justify-center gap-5 bg-bg-alt/95 px-6"
      style={{
        // From 180px down, per the handoff. The board stays visible above it and
        // stays live: the tiles are not dimmed and not disabled.
        top: 180,
        borderTop: '3px solid var(--c-accent)',
        paddingTop: 26,
        // Clears an iPhone's home indicator, which sits exactly where the
        // closing note would otherwise land.
        paddingBottom: 'calc(26px + env(safe-area-inset-bottom))',
      }}
    >
      <div className="flex items-center gap-4">
        {/* Running, and visibly draining. `frozen` is the one variant this file
            may never pass. See the note at the top before changing it. */}
        {inRound ? (
          <LiveClock
            clock={clock}
            size={96}
            variant={reading.urgent ? 'alert' : 'normal'}
            label={`${reading.seconds} seconds left in the round`}
          />
        ) : (
          <SignalBars level={1} />
        )}
        <div className="flex flex-col gap-1.5">
          {inRound ? <p className="label-mono text-accent">round still running</p> : null}
          <h2 className="text-2xl/[1.15] font-bold">Reconnecting</h2>
        </div>
      </div>

      {inRound ? (
        <>
          <p className="max-w-[280px] text-center text-base/[1.5] text-pretty">
            {savedLine(savedAnswerLength)}
          </p>

          <div
            className="flex items-center gap-2.5 rounded-[9px] px-3.5 py-3"
            style={{
              background: 'color-mix(in oklab, var(--c-accent) 18%, var(--c-bg-alt))',
              border: '1px solid color-mix(in oklab, var(--c-accent) 60%, var(--c-bg-alt))',
            }}
          >
            <SignalBars level={1} />
            <p className="font-mono text-xs/[1] font-medium text-accent">
              answer not saved yet, retrying
            </p>
          </div>

          <p className="max-w-[260px] text-center font-mono text-xs/[1.4] text-ink-dim">
            {pauseOnDisconnect
              ? 'Room setting can pause rounds instead. This room does.'
              : 'Room setting can pause rounds instead. This room does not.'}
          </p>
        </>
      ) : null}
    </div>
  );
}
